
import { NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { debounce, isNil, isNumber } from "lodash";
import { Subject, combineLatest } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AvverkningskostnadAtgard, AvverkningskostnadViewmodel } from "~app/features/virkeskop/models/avverkningskostnader/avverkningskostnader-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { AvverkningskostnaderMediatorService } from "~app/features/virkeskop/services/avverkningskostnader-mediator.service";
import { ForkalkylService } from "~app/features/virkeskop/services/forkalkyl.service";
import { ForkalkylmallarService } from "~app/features/virkeskop/services/forkalkylmallar.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VirkeskostnaderMediatorService } from "~app/features/virkeskop/services/virkeskostnader-mediator.service";
import { canEdit } from "~app/role-permissions";
import { TableKeyboardNavigationDirective } from "../../../../../common/directives/table-keyboard-navigation/table-keyboard-navigation.directive";
@Component({
    selector: "rask-atgard-avverkningskostnad",
    templateUrl: "./atgard-avverkningskostnad.component.html",
    styleUrls: ["./atgard-avverkningskostnad.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        TableKeyboardNavigationDirective,
        NgClass,
        InputComponent
    ]
})
export class AtgardAvverkningskostnadComponent implements OnInit, OnDestroy {

    selected: ISelectValue;
    options: ISelectValue[];
    avverkningskostnadAtgard: AvverkningskostnadAtgard;
    avverkningskostnader: AvverkningskostnadViewmodel[];
    atgard: Atgard;

    protected mattslag: string;
    protected kontraktReadonly: boolean;

    private konverteringsfaktor: number;
    private volym: number;
    private readonly ngUnsubscribe$ = new Subject<void>();

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly virkeskostnadMediatorService = inject(VirkeskostnaderMediatorService);
    private readonly forkalkylmallarService = inject(ForkalkylmallarService);
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly avverkningskostnaderMediatorService = inject(AvverkningskostnaderMediatorService);

    private readonly canEdit = canEdit();

    ngOnInit(): void {

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);

        this.forkalkylmallarService.getVardenByGrupp("VK_MATTSLAG").subscribe(mattslag => {
            this.options = mattslag;
            combineLatest([
                this.avverkningskostnaderMediatorService.avverkningskostnader$,
                this.virkeskostnadMediatorService.virkeskostnader$,
                this.atgardMediatorService.atgard$
            ])
                .pipe(
                    filter(([
                        avverkning,
                        virkes,
                        atgard
                    ]) => !isNil(avverkning) && !isNil(virkes) && !isNil(atgard)),
                    takeUntil(this.ngUnsubscribe$))
                .subscribe(([
                    avverkning,
                    virkes,
                    atgard
                ]) => {
                    this.atgard = atgard;
                    this.volym = this.virkeskostnadMediatorService.volymForAtgard(this.atgard?.guid);
                    if (avverkning?.source !== this) {
                        this.avverkningskostnadAtgard = avverkning.getForAtgard(this.atgard?.guid);
                        if (!isNil(this.avverkningskostnadAtgard)) {
                            this.selected = this.options.find(o => o.kod === this.avverkningskostnadAtgard.mattslag);
                            this.avverkningskostnader = this.avverkningskostnadAtgard.avverkningskostnader.map(a => new AvverkningskostnadViewmodel(a));
                            this.setMattslag(this.avverkningskostnadAtgard.mattslag, false);
                        } else {
                            console.error(`Avverkningskostnader saknas för åtgärd id: ${this.atgard?.id} guid: ${this.atgard?.guid}`);
                        }
                    }
                });

        });
    }
    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }
    protected get numberProcessor() {
        return InputProcessorType.numberIntProcessor;
    }

    protected genericItemFormatter = (x: ISelectValue) => x;
    protected genericViewFormatter = (x: ISelectValue) => x ? x.kod : undefined;
    protected onMattslagChange(item: ISelectValue) {
        if (!isNil(item) && item.kod !== this.mattslag) {
            this.setMattslag(item.kod);
            this.avverkningskostnaderMediatorService.update(this.atgard.guid, this.mattslag, null, this);
        }
    }
    protected totalValueChange($event, kostnad: AvverkningskostnadViewmodel): void {
        const debounced = debounce(() => {
            kostnad.kostnadBeraknadTotal = false;
            kostnad.total = isNumber(kostnad.total) ? Math.abs(kostnad.total) : 0;
            this.recalculate(kostnad);
            this.avverkningskostnaderMediatorService.update(this.atgard.guid, this.mattslag, kostnad.avverkningskostnad, this);
        }, 800);
        debounced();
    }
    protected krPerMattslagChanged($event, kostnad: AvverkningskostnadViewmodel): void {
        const debounced = debounce(() => {
            kostnad.kostnadBeraknadTotal = true;
            kostnad.krPerMattslag = isNumber(kostnad.krPerMattslag) ? Math.abs(kostnad.krPerMattslag) : 0;
            this.recalculate(kostnad);
            this.avverkningskostnaderMediatorService.update(this.atgard.guid, this.mattslag, kostnad.avverkningskostnad, this);
        }, 800);
        debounced();
    }
    protected sumKrPerMattslag(): string {
        return this.avverkningskostnader.reduce((k1, k2) => k1 + (isNumber(k2.krPerMattslag) ? k2.krPerMattslag : 0), 0).toLocaleString();
    }
    protected sumKrPerM3Fub(): string {
        return this.avverkningskostnader.reduce((k1, k2) => k1 + (isNumber(k2.krPerM3Fub) ? k2.krPerM3Fub : 0), 0).toLocaleString();
    }
    protected sumTotal(): string {
        return this.avverkningskostnader.reduce((k1, k2) => k1 + (isNumber(k2.total) ? k2.total : 0), 0).toLocaleString();
    }
    private setMattslag(mattslag: string, recalculate = true): void {
        this.mattslag = mattslag;
        this.avverkningskostnadAtgard.mattslag = mattslag;
        this.forkalkylService.getAverageMattslag({
            produktionsomradeId: 26,
            mattslag: mattslag
        }).subscribe(konverteringsfaktor => {
            this.konverteringsfaktor = konverteringsfaktor;

            this.avverkningskostnader.forEach(kostnad => {
                kostnad.konverteringsfaktor = this.konverteringsfaktor;
                if (recalculate) {
                    this.recalculate(kostnad);
                }
            });
        });
    }

    private recalculate(kostnad: AvverkningskostnadViewmodel): void {
        if (kostnad.kostnadBeraknadTotal === true) {
            kostnad.total = Math.round(kostnad.krPerM3Fub * this.volym) ?? 0;
        } else {
            kostnad.krPerMattslag = Math.round(kostnad.total / this.volym / this.konverteringsfaktor);
            if (!isFinite(kostnad.krPerMattslag)) {
                kostnad.krPerMattslag = 0;
            }
        }
    }
}
